import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  TablePagination, 
  Skeleton, Chip, Pagination, Switch
} from '@mui/material';
import { FaPlus as AddIcon, FaSearch as SearchIcon } from 'react-icons/fa';
import { TbCategoryPlus } from "react-icons/tb";
import { MdDeleteForever } from "react-icons/md";
import { postFileUpload, getData, deleteData, putData } from '../../Webservice/Webservice';
import { FiX, FiType, FiUploadCloud, FiTrash2, FiCircle, FiLayers, FiSave } from 'react-icons/fi';
import SUBCATEGORY_ROUTES from '../../API_Route/SubCatagoryRoute';

const PAGE_SIZE = 20; // Default page size

function Subcategories({ categories }) {
  const [subcategories, setSubcategories] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({
    title: '',
    image: '',
    status: 'active',
    category_id: categories[0]?._id || ''
  });
  const [imagePreview, setImagePreview] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pagination state (meta object)
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [allCategories, setAllCategories] = useState([]);

  const fetchAllCategories = async () => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await getData('categories/list', { limit: 1000 }, token);
      setAllCategories(response.categories || []);
    } catch (error) {
      console.error('Failed to fetch all categories:', error);
    }
  };

  useEffect(() => {
    fetchAllCategories();
  }, []);

  // Fetch subcategories with pagination
  const fetchSubcategories = async (page = 1) => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const params = {
        page,
        limit: PAGE_SIZE,
        ...(searchTerm && { search: searchTerm }),
        ...(filterCategory && { category_id: filterCategory }),
        ...(filterStatus && { status: filterStatus })
      };
      
      const response = await getData('subcategories/list', params, token);
      setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
      const meta = response.meta || { page: 1, totalPages: 1, total: 0 };
      setMeta({
        page: meta.page,
        totalPages: meta.totalPages,
        total: meta.total
      });
    } catch (error) {
      setSubcategories([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
      console.error('Fetch subcategories failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubcategories(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, searchTerm, filterCategory, filterStatus]);

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  const handleDeleteSubcategory = (id, subcategory) => {
    setDeleteTarget({ id, subcategory });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        await deleteData(`subcategories/delete/${deleteTarget.id}`, token);
        fetchSubcategories(); // Refresh the list
        handleClose();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleStatusToggle = async (subcategory) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const newStatus = subcategory.status === 'active' ? 'inactive' : 'active';
      await putData(`subcategories/update/${subcategory._id}`, { status: newStatus }, token);
      fetchSubcategories(meta.page); 
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  };

  const handleAddSubcategorySubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('title', newSubcategory.title);
      formData.append('status', newSubcategory.status);
      formData.append('category_id', newSubcategory.category_id);
      if (newSubcategory.image) {
        formData.append('image', newSubcategory.image);
      }
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      await postFileUpload(SUBCATEGORY_ROUTES.ADD_SUBCATEGORY, formData, token);
      
      // Reset form and close dialog
      setAddOpen(false);
      setNewSubcategory({ 
        title: '', 
        image: '', 
        status: 'active', 
        category_id: categories[0]?._id || '' 
      });
      setImagePreview('');
      
      // Refresh subcategories
      fetchSubcategories();
    } catch (error) {
      console.error('Add subcategory failed:', error);
    }
  };

  const handleViewSubcategory = (subcategory) => {
    let categoryId = subcategory.category_id;
    if (categoryId && typeof categoryId === 'object') {
      categoryId = categoryId._id;
    }
    setSelectedSubcategory({ 
      ...subcategory, 
      category_id: categoryId,
      image: subcategory.image // Keep the original image URL
    });
    setViewOpen(true);
  };

  const handleViewSubcategoryClose = () => {
    setViewOpen(false);
    setSelectedSubcategory(null);
  };

  const handleUpdateSubcategory = async () => {
    if (!selectedSubcategory || !selectedSubcategory._id) {
      console.error('No subcategory ID found for update');
      return;
    }
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const hasNewImage = selectedSubcategory.image && selectedSubcategory.image instanceof File;

      if (hasNewImage) {
        const formData = new FormData();
        formData.append('title', selectedSubcategory.title);
        formData.append('status', selectedSubcategory.status);
        formData.append('category_id', selectedSubcategory.category_id);
        formData.append('image', selectedSubcategory.image);
        await putData(`subcategories/update/${selectedSubcategory._id}`, formData, token);
      } else {
        const updateData = {
          title: selectedSubcategory.title,
          status: selectedSubcategory.status,
          category_id: selectedSubcategory.category_id
        };
        await putData(`subcategories/update/${selectedSubcategory._id}`, updateData, token);
      }

      // Refresh subcategories
      fetchSubcategories();
      handleViewSubcategoryClose();
    } catch (error) {
      console.error('Update subcategory failed:', error);
    }
  };

  return (
    <Box flex={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5" component="h1" sx={{
          fontWeight: 700,
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: 'Mulish, sans-serif'
        }}>
          Subcategories Management
          <Chip
            label={`${meta.total} ${meta.total === 1 ? 'Item' : 'Items'}`}
            size="small"
            sx={{ 
                backgroundColor: 'rgba(255, 209, 2, 0.2)', 
                color: '#b39200',
                fontFamily: 'Mulish, sans-serif', 
                fontWeight: '800',
                border: '1px solid rgba(255, 209, 2, 0.5)'
            }}
          />
        </Typography>
        <Button
          variant="contained"
          startIcon={<TbCategoryPlus />}
          sx={{
            fontFamily: 'Mulish, sans-serif',
            background: 'rgb(255 209 2)',
            color: 'black',
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: '0.9rem',
            px: 3,
            boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
            border: '1px solid black',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgb(255 209 2)',
              boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
              transform: 'translateY(-1px)'
            }
          }}
          onClick={() => setAddOpen(true)}
        >
          Add Subcategory
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{
        mb: 3,
        p: 2,
        background: '#f8fafc',
        borderRadius: 2,
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search Subcategories..."
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setMeta({ ...meta, page: 1 }); // Reset to first page when search changes
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>

          {/* Category Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={filterCategory}
                label="Parent Category"
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setMeta({ ...meta, page: 1 });
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {allCategories.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setMeta({ ...meta, page: 1 });
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('');
                setFilterStatus('');
                setMeta({ ...meta, page: 1 });
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                borderColor: 'red',
                color: 'red',
                '&:hover': {
                  borderColor: 'darkred',
                  backgroundColor: 'rgba(255,0,0,0.1)'
                }
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Subcategories Table */}
      <>
          <TableContainer component={Paper}
            elevation={0}
            sx={{
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border:'1px solid #d3d3d3de',
            }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 800,
                    py: 2,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#5c636a',
                    fontFamily: 'Mulish, sans-serif',
                    borderBottom: '2px solid #edeff1',
                  }
                }}>
                  <TableCell>Title</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Skeleton width="60%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rounded" width={40} height={40} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={100} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={160} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={80} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                subcategories.length > 0 ? (
                  subcategories.map((sub) => (
                    <TableRow
                      key={sub._id}
                      hover
                      onClick={() => handleViewSubcategory(sub)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f9fafb' },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell>
                        <Typography 
                          fontWeight={700} 
                          sx={{ 
                            fontFamily: 'Mulish, sans-serif',
                            fontSize: '0.95rem',
                            color: '#1a1c1e',
                            '&:hover': { color: '#0d47a1' }
                          }}
                        >
                          {sub.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <img 
                          src={sub.image} 
                          alt={sub.title} 
                          style={{ 
                            width: 45, 
                            height: 45, 
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            border: '1px solid #e5e7eb'
                          }} 
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch 
                            checked={sub.status === 'active'}
                            size="small"
                            onChange={() => handleStatusToggle(sub)}
                            color="success"
                          />
                          <Chip
                            label={sub.status}
                            size="small"
                            sx={{
                                fontFamily:'Mulish, sans-serif',
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                borderRadius: '6px',
                                backgroundColor: sub.status === 'active' ? '#def7ec' : '#f3f4f6',
                                color: sub.status === 'active' ? '#03543f' : '#4b5563',
                                border: 'none'
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem', color: '#4b5563', fontWeight: 600 }}>
                        {sub.category_id && sub.category_id.title ?
                          sub.category_id.title :
                          'N/A'
                        }
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small"
                          sx={{ 
                              color: '#dc2626',
                              backgroundColor: 'rgba(220, 38, 38, 0.1)',
                              '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' },
                              borderRadius: '8px'
                          }}
                          onClick={e => { 
                            e.stopPropagation(); 
                            handleDeleteSubcategory(sub._id, sub.title); 
                          }}
                        >
                          <MdDeleteForever size={20} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img 
                          src="./img/nodata.png" 
                          alt="No subcategories" 
                          style={{ width: 150, marginBottom: 16 }} 
                        />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          No subcategories found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {searchTerm ? 'No subcategories match your search' : 'Start by adding a new subcategory'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Component (centered, like Categories/ActivityLog) */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
          <Pagination
            count={meta.totalPages}
            page={meta.page}
            onChange={handlePageChange}
            sx={{
                '& .MuiPaginationItem-root': {
                    fontFamily: 'Mulish, sans-serif',
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                        backgroundColor: 'rgb(255, 209, 2)',
                        color: 'black',
                        '&:hover': {
                            backgroundColor: 'rgb(255, 190, 0)',
                        }
                    }
                }
            }}
            shape="rounded"
            disabled={loading}
            showFirstButton
            showLastButton
          />
        </Box>
        {meta.total > 0 && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
          </Typography>
        )}
      </>

      {/* Add Subcategory Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FiLayers size={20} />
            Add New Subcategory
          </Box>
          <IconButton
            onClick={() => setAddOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3,mt:2 }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Subcategory Name"
              value={newSubcategory.title}
              onChange={e => setNewSubcategory({ ...newSubcategory, title: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiType color="#6c757d" size={18} />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px' }
              }}
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Subcategory Image
              </Typography>

              <Box
                component="label"
                htmlFor="subcategory-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview('');
                        setNewSubcategory({ ...newSubcategory, image: '' });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="subcategory-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewSubcategory({ ...newSubcategory, image: file });
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newSubcategory.status}
                label="Status"
                onChange={e => setNewSubcategory({ ...newSubcategory, status: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category Selector */}
            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={newSubcategory.category_id}
                label="Parent Category"
                onChange={e => setNewSubcategory({ ...newSubcategory, category_id: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                {allCategories.filter(cat => cat && cat._id && cat.title).map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FiLayers size={14} color="#6c757d" />
                      {cat.title}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
          <Button
            onClick={() => setAddOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              color: 'grey',
              borderColor:"grey"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSubcategorySubmit}
            disabled={!newSubcategory.title || !newSubcategory.category_id}
            sx={{
              border:'1px solid black',
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              color: 'black',
              '&:hover': {
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Create Subcategory
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subcategory Dialog */}
      <Dialog
        open={viewOpen}
        onClose={handleViewSubcategoryClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FiLayers size={20} />
            Edit Subcategory
          </Box>
          <IconButton
            onClick={handleViewSubcategoryClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            <FiX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 3 }}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt:2 }}>
            {/* Title Field */}
            <TextField
              variant="outlined"
              label="Subcategory Name"
              value={selectedSubcategory?.title || ''}
              onChange={e => setSelectedSubcategory({ ...selectedSubcategory, title: e.target.value })}
              fullWidth
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <FiUploadCloud size={16} />
                Subcategory Image
              </Typography>

              <Box
                component="label"
                htmlFor="edit-subcategory-image-upload"
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 123, 255, 0.05)'
                  }
                }}
              >
                {selectedSubcategory?.image ? (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={selectedSubcategory.image instanceof File ?
                        URL.createObjectURL(selectedSubcategory.image) :
                        selectedSubcategory.image}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 6
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSubcategory({ ...selectedSubcategory, image: null });
                      }}
                    >
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <FiUploadCloud size={48} color="#adb5bd" style={{ marginBottom: '8px' }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        Click to upload
                      </Box> or drag and drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      PNG, JPG, GIF (Max 2MB)
                    </Typography>
                  </>
                )}

                <input
                  id="edit-subcategory-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedSubcategory({ ...selectedSubcategory, image: file });
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Status Selector */}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedSubcategory?.status || 'active'}
                label="Status"
                onChange={e => setSelectedSubcategory({ ...selectedSubcategory, status: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#28a745" />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiCircle size={14} color="#6c757d" />
                    Inactive
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Category Selector */}
            <FormControl fullWidth>
              <InputLabel>Parent Category</InputLabel>
              <Select
                value={selectedSubcategory?.category_id || ''}
                label="Parent Category"
                onChange={e => setSelectedSubcategory({ ...selectedSubcategory, category_id: e.target.value })}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                {allCategories.filter(cat => cat && cat._id && cat.title).map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FiLayers size={14} color="#6c757d" />
                      {cat.title}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
          <Button
            onClick={handleViewSubcategoryClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              color:'red',
              borderColor: 'red',
              '&:hover': {
                borderColor: 'darkred',
                backgroundColor: 'rgba(255,0,0,0.1)'}
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSubcategory}
            disabled={!selectedSubcategory?.title || !selectedSubcategory?.category_id}
            startIcon={<FiSave size={18} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: 'none',
              background: 'rgb(255 209 2)',
              border: '1px solid black',
              fontFamily: 'Mulish, sans-serif',
              color: 'black',
              '&:hover': {
                background: 'rgb(255 209 2)'
              },
              '&:disabled': {
                background: '#e9ecef',
                color: '#adb5bd'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: '10px',
            padding: '20px',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
          <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
            Are you sure you want to delete subcategory <b>{deleteTarget?.subcategory}</b>?
          </DialogContentText>
          <Box sx={{
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            padding: '12px',
            borderRadius: '4px',
            borderLeft: '4px solid #ffc107',
            marginBottom: '2px'
          }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
              Warning
            </Typography>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              By deleting, you won't be able to access it again.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              minWidth: '120px',
              borderColor: 'gray',
              color: 'gray',
              '&:hover': {
                borderColor: 'darkgray',
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              minWidth: '120px',
              backgroundColor: 'red',
              '&:hover': {
                backgroundColor: '#bb2d3b'
              }
            }}
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Subcategories;